import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { useRun } from "@/state/run";
import { useSceneStore } from "@/engine/SceneManager";
import { useForgePickerStore } from "@/state/forgePicker";
import { useAudio } from "@/state/audio";

/**
 * HUD 우하단 캐릭터 초상 앵커.
 * static resource 는 /public/art/characters/{classCardId}/ 하위 face.png 또는 portrait.png.
 * face.png 는 450px 원형 크롭, portrait.png 는 전신. HUD 는 원형 우선.
 *
 * <p>2026-04-24: "다음에 할 일" 말풍선 제거. 사용자 피드백 — 상단 배너 / 상황 드로어와
 * 중복 노출돼 "전단지 같은 느낌" 을 유발. 필요 시 SituationDrawer 에서 확인 가능.
 */
export function PortraitAnchor() {
    const run = useRun(s => s.run);
    const [menuOpen, setMenuOpen] = useState(false);
    const push = useSceneStore(s => s.push);

    // 🚨 2026-04-23 크리티컬 버그 수정: Hook Rules 위반.
    //   이전 코드는 `if (!run) return null;` 을 useEffect 앞에 두어, run 유무에 따라
    //   hook count 가 달라졌다. 매 re-render 마다 React #310 ("Rendered more hooks
    //   than during the previous render") 발생 → 전역 HUD 트리 crash → SituationDrawer
    //   누락 · 결과창 먹통 · 씬 전환 중간 실패 · 캐릭터 튀는 현상 등 모든 증상의 진짜 원인.
    //   Hook 은 반드시 early return 보다 먼저 모두 호출해야 함.
    // 2026-04-25: zoomOpen 분리 모달 제거 — menu 모달 안에 portrait 를 같이 띄움.
    //   "초상 크게 보기" 버튼이 별 의미 없는 한 단계를 강요했다.
    useEffect(() => {
        if (!menuOpen) return;
        const handler = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setMenuOpen(false);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [menuOpen]);

    if (!run) return null;

    const cardId = run.classCard.id;
    const initial = run.characterName.charAt(0);

    return (
        <>
            <div className="relative">
                <motion.button
                    type="button"
                    onClick={() => setMenuOpen(true)}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.01, y: -1 }}
                    transition={{ duration: 0.8, ease: tokens.easing.decelerate }}
                    className="relative rounded-l overflow-hidden text-left group"
                    style={{
                        width: 260,
                        height: 360,
                        background: `radial-gradient(ellipse at 50% 30%, ${tokens.color.burgundy}66 0%, ${tokens.color.ink} 70%)`,
                        border: `1px solid ${tokens.color.brass}88`,
                        boxShadow: `0 0 32px ${tokens.color.ember}33, inset 0 0 60px ${tokens.color.ink}, inset 0 1px 0 ${tokens.color.brass}44`,
                        cursor: "zoom-in",
                    }}
                >
                    <img
                        src={`/art/characters/${cardId}/portrait.png`}
                        alt={run.classCard.name}
                        onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            if (!img.dataset.fallback) {
                                img.dataset.fallback = "1";
                                img.src = `/art/characters/${cardId}/face.png`;
                            } else {
                                img.style.visibility = "hidden";
                            }
                        }}
                        className="absolute inset-0 w-full h-full object-cover object-top"
                    />
                    <div
                        className="absolute inset-0 flex items-center justify-center font-display"
                        style={{
                            color: `${tokens.color.ember}30`,
                            fontSize: "160px",
                            letterSpacing: tokens.type.letterSpacing.tight,
                            pointerEvents: "none",
                        }}
                    >
                        {initial}
                    </div>
                    <div
                        className="absolute right-s top-s rounded-pill px-s py-[2px] font-display"
                        style={{
                            color: tokens.color.parchment,
                            background: "rgba(14,12,24,0.68)",
                            border: `1px solid ${tokens.color.parchment}33`,
                            fontSize: 13.5,
                            letterSpacing: tokens.type.letterSpacing.wide,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        인물 메뉴
                    </div>
                    {/* 2026-04-23: 초상 하단 그라디언트 — 이름이 빛 속에서 뜨는 느낌. */}
                    <div
                        aria-hidden
                        className="absolute inset-x-0 bottom-0 pointer-events-none"
                        style={{
                            height: "48%",
                            background: `linear-gradient(180deg, transparent 0%, ${tokens.color.ink}88 45%, ${tokens.color.ink}F2 100%)`,
                        }}
                    />
                    {/* 2026-04-23: 하단 금속 hair-line — 이름 위에 가느다란 분할선. */}
                    <div
                        aria-hidden
                        className="absolute pointer-events-none"
                        style={{
                            left: 16,
                            right: 16,
                            bottom: 68,
                            height: 1,
                            background: `linear-gradient(90deg, transparent 0%, ${tokens.color.brass}AA 30%, ${tokens.color.brass}66 70%, transparent 100%)`,
                            boxShadow: `0 0 6px ${tokens.color.brass}33`,
                        }}
                    />
                    <div
                        className="absolute left-m right-m bottom-m"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: tokens.type.size.small,
                        }}
                    >
                        <div
                            className="font-body"
                            style={{
                                color: tokens.color.parchment,
                                fontSize: 13.5,
                                letterSpacing: "0.22em",
                                opacity: 0.78,
                                marginBottom: 2,
                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                            }}
                        >
                            원정의 주인
                        </div>
                        <div
                            className="font-display"
                            style={{
                                color: tokens.color.parchment,
                                letterSpacing: "0.04em",
                                fontSize: "clamp(22px, 1.7vw, 26px)",
                                textShadow: `0 0 14px ${tokens.color.ember}55, 0 1px 0 ${tokens.color.ink}`,
                                lineHeight: 1.1,
                            }}
                        >
                            {run.characterName}
                        </div>
                        <div
                            className="font-body mt-[3px]"
                            style={{
                                color: tokens.color.parchment,
                                fontSize: 13.5,
                                letterSpacing: "0.04em",
                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                            }}
                        >
                            {run.classCard.name} · {run.scenario.name}
                        </div>
                    </div>
                </motion.button>
            </div>

            {menuOpen && (
                <div
                    className="fixed inset-0 flex items-center justify-center p-l"
                    style={{
                        background: `${tokens.color.ink}D5`,
                        backdropFilter: "blur(5px)",
                        zIndex: tokens.elevation.modal,
                    }}
                    onClick={() => setMenuOpen(false)}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        className="rounded-l overflow-hidden flex"
                        style={{
                            // 2026-04-25: 양쪽으로 portrait + menu 한 번에 — 이전엔 menu 안에 "초상 크게 보기"
                            //   가 한 단계 더 강요했지만, 옆에 같이 띄우면 한 모달에서 다 됨.
                            width: "min(1100px, 92vw)",
                            maxHeight: "88vh",
                            background: `linear-gradient(180deg, rgba(18,14,26,0.96) 0%, rgba(11,9,18,0.98) 100%)`,
                            border: `1px solid ${tokens.color.brass}55`,
                            boxShadow: `0 24px 60px ${tokens.color.ink}88`,
                        }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {/* 좌측: 큰 초상 */}
                        <div
                            className="flex-shrink-0 relative"
                            style={{
                                width: "min(560px, 50%)",
                                background: "radial-gradient(circle at 50% 22%, rgba(92,31,46,0.32) 0%, rgba(14,12,24,0.96) 72%)",
                                borderRight: `1px solid ${tokens.color.parchment}1a`,
                            }}
                        >
                            <img
                                src={`/art/characters/${cardId}/portrait.png`}
                                alt={run.classCard.name}
                                onError={(e) => {
                                    const img = e.target as HTMLImageElement;
                                    if (!img.dataset.fallback) {
                                        img.dataset.fallback = "1";
                                        img.src = `/art/characters/${cardId}/face.png`;
                                    } else {
                                        img.style.visibility = "hidden";
                                    }
                                }}
                                className="block w-full h-full object-contain"
                                style={{ maxHeight: "88vh" }}
                            />
                        </div>

                        {/* 우측: 인물 정보 + 메뉴 */}
                        <div className="flex-1 flex flex-col p-l overflow-y-auto">
                            <div className="flex items-start justify-between gap-m">
                                <div>
                                    <div
                                        className="font-body"
                                        style={{
                                            color: tokens.color.parchmentDim,
                                            fontSize: 14.5,
                                            letterSpacing: "0.12em",
                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                        }}
                                    >
                                        원정의 주인
                                    </div>
                                    <div
                                        className="font-display mt-[2px]"
                                        style={{
                                            color: tokens.color.parchment,
                                            fontSize: 30,
                                            letterSpacing: "0.04em",
                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                        }}
                                    >
                                        {run.characterName}
                                    </div>
                                    <div
                                        className="font-body mt-[6px]"
                                        style={{
                                            color: tokens.color.parchment,
                                            fontSize: 14,
                                            opacity: 0.8,
                                            letterSpacing: "0.04em",
                                        }}
                                    >
                                        {run.classCard.name} · {run.scenario.name}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setMenuOpen(false)}
                                    className="rounded-pill px-s py-xs font-display"
                                    style={{
                                        color: tokens.color.parchment,
                                        background: `${tokens.color.ink}B8`,
                                        border: `1px solid ${tokens.color.parchment}2E`,
                                        fontSize: 13.5,
                                        letterSpacing: tokens.type.letterSpacing.wide,
                                        cursor: "pointer",
                                    }}
                                >
                                    닫기
                                </button>
                            </div>
                            <div className="mt-l grid gap-s">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        void push("character-sheet", { runId: run.id });
                                    }}
                                    className="rounded-m px-m py-m text-left"
                                    style={{
                                        background: `${tokens.color.ember}10`,
                                        border: `1px solid ${tokens.color.ember}33`,
                                        cursor: "pointer",
                                    }}
                                >
                                    <div
                                        className="font-display"
                                        style={{
                                            color: tokens.color.parchment,
                                            fontSize: 18,
                                            letterSpacing: "0.05em",
                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                        }}
                                    >
                                        인물 기록 열기
                                    </div>
                                    <div
                                        className="font-body mt-[2px]"
                                        style={{
                                            color: tokens.color.parchment,
                                            fontSize: 14.5,
                                            lineHeight: 1.45,
                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                        }}
                                    >
                                        스탯, 장비, 지난 전투와 기록을 여기서 엽니다.
                                    </div>
                                </button>
                                {/* 2026-04-25: 사용자 요청 — 제련/각인 진입로를 인물 메뉴에 통합.
                                    Portrait → 제련소 / 각인 의식 클릭 → 장비 picker overlay 진입.
                                    onForgePicker 는 부모(HudLayer 또는 sceneRoot) 에서 ForgePickerOverlay 를 띄움. */}
                                <div className="grid grid-cols-2 gap-s">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            useForgePickerStore.getState().open("refine");
                                        }}
                                        className="rounded-m px-m py-s text-left"
                                        style={{
                                            background: `${tokens.color.brass}10`,
                                            border: `1px solid ${tokens.color.brass}44`,
                                            cursor: "pointer",
                                        }}
                                    >
                                        <div
                                            className="font-display"
                                            style={{
                                                color: tokens.color.parchment,
                                                fontSize: 15,
                                                letterSpacing: "0.04em",
                                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                            }}
                                        >
                                            🔨 제련소
                                        </div>
                                        <div
                                            className="font-body mt-[2px]"
                                            style={{
                                                color: tokens.color.parchmentDim,
                                                fontSize: 12,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            장비를 두드려 강화
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            useForgePickerStore.getState().open("engrave");
                                        }}
                                        className="rounded-m px-m py-s text-left"
                                        style={{
                                            background: `${tokens.color.moss}10`,
                                            border: `1px solid ${tokens.color.moss}44`,
                                            cursor: "pointer",
                                        }}
                                    >
                                        <div
                                            className="font-display"
                                            style={{
                                                color: tokens.color.parchment,
                                                fontSize: 15,
                                                letterSpacing: "0.04em",
                                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                            }}
                                        >
                                            ✦ 각인 의식
                                        </div>
                                        <div
                                            className="font-body mt-[2px]"
                                            style={{
                                                color: tokens.color.parchmentDim,
                                                fontSize: 12,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            무기에 이름 새기기
                                        </div>
                                    </button>
                                </div>
                                {/* 2026-04-25: 인게임 음량 — 사용자 요청. 설정 씬으로 안 빠져나가도 바로 조절. */}
                                <InlineVolumeControl />
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </>
    );
}

/**
 * 2026-04-25: 인게임에서도 음량을 조절할 수 있도록 PortraitAnchor 메뉴 안에 인라인 컨트롤.
 *   기존엔 SettingsScene 으로 빠져나가야 했음 — 인게임 흐름 끊김.
 *   master / bgm / sfx 각각 슬라이더 + 음소거 토글.
 */
function InlineVolumeControl() {
    const audio = useAudio();
    return (
        <div
            className="rounded-m px-m py-s mt-xs"
            style={{
                background: `${tokens.color.parchment}06`,
                border: `1px solid ${tokens.color.parchmentDim}28`,
            }}
        >
            <div
                className="flex items-center justify-between font-body"
                style={{ marginBottom: 6 }}
            >
                <span style={{ color: tokens.color.parchmentDim, fontSize: 12, letterSpacing: "0.18em" }}>
                    음량
                </span>
                <button
                    type="button"
                    onClick={() => audio.setMuted(!audio.muted)}
                    className="rounded-pill px-s"
                    style={{
                        background: audio.muted ? `${tokens.color.burgundy}33` : `${tokens.color.parchment}10`,
                        border: `1px solid ${audio.muted ? tokens.color.burgundy : tokens.color.parchmentDim}55`,
                        color: tokens.color.parchment,
                        fontSize: 11,
                        letterSpacing: "0.1em",
                        cursor: "pointer",
                        padding: "2px 10px",
                    }}
                >
                    {audio.muted ? "음소거 해제" : "음소거"}
                </button>
            </div>
            <VolumeSlider label="전체" value={audio.master} onChange={audio.setMaster} />
            <VolumeSlider label="배경" value={audio.bgm} onChange={audio.setBgm} />
            <VolumeSlider label="효과" value={audio.sfx} onChange={audio.setSfx} />
        </div>
    );
}

function VolumeSlider({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
    return (
        <div className="flex items-center gap-s mb-[4px]">
            <div
                className="font-body"
                style={{ color: tokens.color.parchment, fontSize: 12, minWidth: 32, opacity: 0.78 }}
            >
                {label}
            </div>
            <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={value}
                onChange={e => onChange(parseFloat(e.target.value))}
                className="flex-1"
                style={{ accentColor: tokens.color.ember }}
            />
            <div
                className="font-stat text-right"
                style={{ color: tokens.color.parchmentDim, fontSize: 11, minWidth: 32 }}
            >
                {Math.round(value * 100)}
            </div>
        </div>
    );
}
